<template>
	<slot v-if="ready" />
</template>

<script setup lang="ts">
import { inject, onMounted, onBeforeUnmount, provide, ref, watch } from "vue";
import {
	isLoadedSymbol,
	mapSymbol,
	sourceIdSymbol,
	sourceLayerRegistry,
} from "vue-maplibre-gl";
import { MaplibreTerradrawControl } from "@watergis/maplibre-gl-terradraw";
import type { TerradrawMode } from "@watergis/maplibre-gl-terradraw";
import "@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css";
import {
	HexColor,
	TerraDrawFreehandMode,
	TerraDrawPolygonMode,
	TerraDrawSelectMode,
	type GeoJSONStoreFeatures,
} from "terra-draw";

type FeatureId = string | number;

interface OnFinishEvent {
	mode: string;
	action:
		| "draw"
		| "edit"
		| "deleteCoordinate"
		| "insertMidpoint"
		| "dragCoordinate"
		| "dragFeature"
		| "dragCoordinateResize";
}

interface OnChangeEvent {
	origin?: "api";
	target?: "geometry" | "properties";
}

const props = defineProps<{
	sourceId: string;
	controlPosition?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
	showControl?: boolean;
	activeMode?: string | null;
	// Custom select modes (e.g. "move", "edit") are registered alongside the
	// stock modes, so the name set is extensible beyond TerradrawMode.
	modes?: (TerradrawMode | (string & {}))[];
	modelValue: GeoJSON.Feature[];
}>();

const emit = defineEmits<{
	create: [feature: GeoJSONStoreFeatures];
	delete: [feature: GeoJSONStoreFeatures | undefined];
	deselect: [feature: GeoJSONStoreFeatures];
	select: [feature: GeoJSONStoreFeatures];
	styling: [feature: GeoJSON.Feature];
	update: [feature: GeoJSONStoreFeatures];
	"update:modelValue": [features: GeoJSON.Feature[]];
}>();
const handlers = new Map<string, () => void>();
const registry = {
	registerUnmountHandler: (id: string, handler: () => void) => {
		handlers.set(id, handler);
	},
	unregisterUnmountHandler: (id: string) => {
		handlers.delete(id);
	},
	unmount: () => {
		handlers.forEach((h) => h());
		handlers.clear();
	},
} as any;

provide(sourceIdSymbol, props.sourceId);
provide(sourceLayerRegistry, registry);

const drawControl = ref<MaplibreTerradrawControl | null>(null);
const featureUUIDToFeature = new Map<FeatureId, GeoJSONStoreFeatures>();
const isMapLoaded = inject(isLoadedSymbol)!;
const map = inject(mapSymbol)!;
const ready = ref(false);

/**
 * The features this component last handed the parent, serialised.
 *
 * It is what tells a parent's echo apart from a parent's own change. A flag
 * cleared on a timer cannot: a `create` event notifies the parent synchronously
 * and Vue flushes the resulting prop update in a microtask, so the flag is still
 * up when the parent's change arrives — and a change that *removes* the shape
 * just drawn (a transient draw handing its ring to a caller that redraws it)
 * would be dropped on the floor. Comparing the content says exactly what is
 * meant: if the parent is repeating what we sent, there is nothing to do; if it
 * is saying something else, the store follows the parent.
 */
let lastEmitted: string | null = null;

/** The identity of a feature set, for "is this the same list" — id, geometry and
 * properties, in order, so a re-order is a change and a fresh array of the same
 * features is not. */
function featureSignature(features: GeoJSON.Feature[]): string {
	return JSON.stringify(
		features.map((feature) => [
			feature.id ?? null,
			feature.geometry,
			feature.properties ?? {},
		]),
	);
}

watch(
	() => props.modelValue,
	() => {
		const ctrl = drawControl.value;
		if (!ctrl) return;
		const inst = ctrl.getTerraDrawInstance();
		if (!inst) return;
		if (
			lastEmitted !== null &&
			featureSignature(props.modelValue) === lastEmitted
		) {
			return;
		}
		inst.clear();
		if (props.modelValue.length > 0) {
			addFeaturesChecked(inst, props.modelValue);
		}
	},
);

function applyActiveMode(inst: { setMode: (mode: string) => void }) {
	const mode = props.activeMode ?? "render";
	try {
		inst.setMode(mode);
	} catch {
		// A mode the control did not register is not a reason to break the map.
	}
}

watch(
	() => props.activeMode,
	() => {
		const inst = drawControl.value?.getTerraDrawInstance();
		if (inst) applyActiveMode(inst);
	},
);

function initSource() {
	if (!map.value) return;
	map.value.addSource(props.sourceId, {
		type: "geojson",
		data: { type: "FeatureCollection", features: [] },
	});

	ready.value = true;

	const ctrl = new MaplibreTerradrawControl({
		modes: (props.modes ?? [
			"render",
			"polygon",
			"rectangle",
			"circle",
			"freehand",
			"select",
			"move",
			"edit",
		]) as unknown as TerradrawMode[],
		modeOptions: {
			freehand: new TerraDrawFreehandMode({
				styles: {
					fillColor: (feature) =>
						(feature.properties?.color as HexColor) ?? "#3b82f6",
					fillOpacity: (feature) =>
						(feature.properties?.opacity as number) ?? 0.3,
					outlineColor: (feature) =>
						(feature.properties?.color as HexColor) ?? "#1d4ed8",
					outlineWidth: 2,
				},
			}),
			polygon: new TerraDrawPolygonMode({
				editable: true,
				styles: {
					fillColor: (feature) =>
						(feature.properties?.color as HexColor) ?? "#3b82f6",
					fillOpacity: (feature) =>
						(feature.properties?.opacity as number) ?? 0.3,
					outlineColor: (feature) =>
						(feature.properties?.color as HexColor) ?? "#1d4ed8",
					outlineWidth: 2,
				},
			}),
			// Dedicated "move" (translate-only) and "edit" (vertex-only) select
			// modes for polygons. Previously both translation and vertex
			// manipulation lived in the default "select" mode, where coordinate
			// dragging took priority over feature translation, making moving a
			// pool polygon awkward. Splitting them lets reviewers translate
			// without accidentally grabbing a vertex.
			move: new TerraDrawSelectMode({
				modeName: "move",
				flags: {
					polygon: {
						feature: { draggable: true },
					},
				},
			}),
			edit: new TerraDrawSelectMode({
				modeName: "edit",
				flags: {
					polygon: {
						feature: {
							coordinates: {
								draggable: true,
								midpoints: true,
								deletable: true,
							},
						},
					},
				},
			}),
		},
		open: true,
	});

	map.value.addControl(ctrl, props.controlPosition ?? "bottom-right");
	if (props.showControl === false) {
		const container = (ctrl as unknown as { controlContainer?: HTMLElement })
			.controlContainer;
		if (container) container.style.display = "none";
	}
	drawControl.value = ctrl;

	// kick setup forward to the next tick since the system needs a moment
	// to create the Terradraw instance set up
	setTimeout(() => {
		const inst = ctrl.getTerraDrawInstance();
		if (!inst) {
			console.error("terra draw instance not ready yet");
			return;
		}
		inst.on("change", handleChange);
		inst.on("deselect", handleDeselect);
		inst.on("finish", handleFinish);
		//inst.on("history", handleHistory);
		//inst.on("ready", handleReady);
		inst.on("select", handleSelect);
		inst.start();
		applyActiveMode(inst);

		if (props.modelValue.length > 0) {
			addFeaturesChecked(inst, props.modelValue);
			syncFeatures();
		}
	}, 0);
}

watch(
	isMapLoaded,
	(loaded) => {
		if (loaded) initSource();
	},
	{ immediate: true },
);
// TerraDraw stores transient editing helpers (vertex/midpoint/snapping
// handles) as real features in the store. These must not leak into the
// parent's drawn-features model or the map source.
function isGuidanceFeature(f: GeoJSONStoreFeatures): boolean {
	const p = f.properties ?? {};
	return Boolean(
		p.midPoint || p.selectionPoint || p.coordinatePoint || p.snappingPoint,
	);
}

function currentFeatures(): GeoJSON.Feature[] | undefined {
	const ctrl = drawControl.value;
	if (!map.value || !ctrl) return;

	const instance = ctrl.getTerraDrawInstance();
	if (!instance) return;

	const snapshot = instance.getSnapshot();
	const features = snapshot
		.filter((f) => !isGuidanceFeature(f))
		.map((f) => ({
			id: f.id,
			geometry: f.geometry as GeoJSON.Geometry,
			properties: f.properties ?? {},
			type: "Feature" as const,
		}));
	return features;
}
function featureByID(id: FeatureId): GeoJSONStoreFeatures | undefined {
	const ctrl = drawControl.value;
	if (!map.value || !ctrl) return;

	const instance = ctrl.getTerraDrawInstance();
	if (!instance) return;

	return instance.getSnapshotFeature(id) as GeoJSONStoreFeatures | undefined;
}
function handleChange(ids: FeatureId[], type: string, context?: OnChangeEvent) {
	// We track when things are created internally so we can track deletions
	if (type === "create") {
		for (const id of ids) {
			const f = featureByID(id);
			if (f) featureUUIDToFeature.set(id, f);
		}
	} else if (type === "delete" && context?.origin === "api") {
		for (const id of ids) {
			const feature = featureUUIDToFeature.get(id);
			emit("delete", feature);
		}
	} else if (type === "styling") {
	} else if (type === "update") {
		// We don't handle updates here because we get them at the UI's draw
		// rate, which is just...not helpful. Instead we take updates via the "finish" event.
	}
	syncFeatures();
}
function handleDeselect(id: FeatureId) {
	const f = featureByID(id);
	if (f) emit("deselect", f);
	syncFeatures();
}
function handleFinish(id: FeatureId, context: OnFinishEvent) {
	const feature = featureByID(id);
	if (!feature) return;
	if (context.action === "draw") {
		emit("create", feature);
	} else if (
		context.action === "dragFeature" ||
		context.action === "dragCoordinate" ||
		context.action === "dragCoordinateResize" ||
		context.action === "insertMidpoint"
	) {
		emit("update", feature);
	}
	syncFeatures();
}
function handleSelect(id: FeatureId) {
	const f = featureByID(id);
	if (f) emit("select", f);
	syncFeatures();
}
function syncFeatures() {
	const features = currentFeatures();
	if (!features) return;
	if (!(map && map.value)) return;
	const source = map.value.getSource(props.sourceId) as
		| maplibregl.GeoJSONSource
		| undefined;
	if (source) {
		source.setData({
			type: "FeatureCollection",
			features,
		});
	}

	// Record what the parent is being told, so its echo can be recognised by
	// content when the prop comes back (see `lastEmitted`).
	lastEmitted = featureSignature(features);
	emit("update:modelValue", features);
}
onBeforeUnmount(() => {
	const ctrl = drawControl.value;
	drawControl.value = null;
	if (map.value?.style) {
		if (ctrl) {
			const inst = ctrl.getTerraDrawInstance();
			if (inst) {
				inst.stop();
			}
			map.value.removeControl(ctrl);
		}
		map.value.removeSource(props.sourceId);
	}
	registry.unmount();
});

function clear() {
	const ctrl = drawControl.value;
	if (!ctrl) return;
	const instance = ctrl.getTerraDrawInstance();
	if (instance) {
		instance.clear();
	}
	syncFeatures();
}

type StoreValidation = { valid: boolean; reason?: string };

function roundCoords(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
	const rounded = JSON.parse(
		JSON.stringify(geometry, (_key, val) =>
			typeof val === "number" ? Math.round(val * 1e7) / 1e7 : val,
		),
	);
	return rounded;
}

function addFeaturesChecked(
	instance: { addFeatures: (features: any[]) => any },
	features: GeoJSON.Feature[],
) {
	const storeFeatures: any[] = [];
	for (const f of features) {
		const mode = (f.properties as any)?.mode || "polygon";
		const props = { ...((f.properties as any) || {}), mode };
		const geom = f.geometry;

		if (geom.type === "MultiPolygon") {
			for (const coords of geom.coordinates) {
				storeFeatures.push({
					type: "Feature",
					geometry: roundCoords({ type: "Polygon", coordinates: coords }),
					properties: props,
				});
			}
		} else if (geom.type === "Polygon") {
			storeFeatures.push({
				...f,
				geometry: roundCoords(geom),
				properties: props,
			});
		}
	}

	const results = instance.addFeatures(
		storeFeatures as any,
	) as StoreValidation[];
	const failures = results.filter((r) => !r.valid);
	if (failures.length > 0) {
		console.error(
			"TerraDraw addFeatures validation failures:",
			failures.map((f) => f.reason ?? "unknown").join(", "),
		);
	}
}

defineExpose({
	switchMode(mode: TerradrawMode | string) {
		const instance = drawControl.value?.getTerraDrawInstance();
		if (instance) {
			instance.setMode(mode as string);
		}
	},
	selectFeature(id: FeatureId) {
		const instance = drawControl.value?.getTerraDrawInstance();
		if (instance) {
			instance.selectFeature(id);
		}
	},
	getTerraDrawInstance() {
		return drawControl.value?.getTerraDrawInstance() ?? null;
	},
});
</script>

<style>
/* Custom mode buttons have no stock icon; provide Material-style SVGs that
   match the @watergis/maplibre-gl-terradraw control's existing icon rules. */
.maplibregl-terradraw-add-move-button {
	background: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='%235f6368' viewBox='0 0 24 24'%3E%3Cpath d='M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2l-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z'/%3E%3C/svg%3E");
	background-position: 50%;
	background-repeat: no-repeat;
	background-size: 90%;
}
.maplibregl-terradraw-add-edit-button {
	background: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='%235f6368' viewBox='0 0 24 24'%3E%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'/%3E%3C/svg%3E");
	background-position: 50%;
	background-repeat: no-repeat;
	background-size: 90%;
}
</style>
